// The FIG card section's body: headline, by category, the charges with no
// receipt (FAMILY_Receipts_Spec_v2 §9 "FIG Missing Receipts"), the charges
// with one (each opens its receipt; Personal marked), and what was left out.
// Read only. The whole business card, whatever the person filter says. A
// charge whose receipt is Personal is personal on the FIG card: the headline
// counts business only, and personal has its own line that opens Covered on
// just those rows.
import { useState } from 'react';
import { usd, shortDate } from './money';
import { figHeadline, figSplit, categoryRows, coveredLine, leftOutLine, missingFoot, isPersonal, personalLine, figNote } from './figCoverageModel';

const PERSONAL_TITLE = 'Personal spend on the FIG card: review with the CPA';

/**
 * fig: { status: 'loading' | 'ok' | 'error', data, error }. needsRange: the
 * dates are All time (the server needs a from and a to).
 */
export default function FigCoverage({ fig, needsRange = false, onRetry, onOpenReceipt }) {
  const [coveredOpen, setCoveredOpen] = useState(false);
  const [onlyPersonal, setOnlyPersonal] = useState(false);
  if (needsRange) return <p className="rc-note rc-fig-state">Pick a date range to check the FIG card.</p>;
  if (!fig || fig.status === 'loading') return <p className="rc-note rc-fig-state">Checking the FIG card…</p>;
  if (fig.status === 'error') {
    return (
      <p className="rc-error rc-fig-state">⚠ Couldn't check the FIG card ({fig.error}). {onRetry && <button type="button" className="rc-anchor" onClick={onRetry}>Retry</button>}</p>
    );
  }
  const cov = fig.data;
  const head = figHeadline(cov);
  const left = leftOutLine(cov);
  const leftOut = left && (
    <details className="rc-missing rc-fig-left">
      <summary>{left}</summary>
      <ul className="rc-fig-rules">{(cov.excluded.byRule || []).map((r) => (
        <li key={r.rule}><span>{r.rule}</span><span className="rc-nowrap">{r.charges} · {usd(r.dollars)}</span></li>
      ))}</ul>
    </details>
  );
  if (!head) return <><p className="rc-note rc-fig-state">No FIG card charges in this range.</p>{leftOut}</>;
  const cats = categoryRows(cov);
  const split = figSplit(cov);
  const personal = personalLine(cov);
  const missing = cov.missing || [];
  const covered = cov.covered || [];
  const shown = onlyPersonal ? covered.filter(isPersonal) : covered;
  const shareOf = (x) => (split.all.dollars > 0 ? Math.max(0, Math.min(1, x / split.all.dollars)) * 100 : 0);
  return (
    <div className="rc-fig">
      {head.businessNone ? <div className="rc-fig-head">No business charges in this range.</div>
        : <div className="rc-fig-head"><strong>{head.withReceipt} of {head.charges}</strong> business charges have a receipt · <strong>{usd(head.coveredDollars)} of {usd(head.chargeDollars)}</strong> · {Math.round(head.share * 100)}%</div>}
      {personal && (
        <button type="button" className="rc-tag rc-tag--amber rc-fig-personal" onClick={() => { setOnlyPersonal(true); setCoveredOpen(true); }}>{personal}</button>
      )}
      <div className="rc-band" aria-hidden="true">
        <span style={{ width: `${shareOf(split.business.coveredDollars)}%`, background: '#2e8b57' }} title={`Business with a receipt ${usd(split.business.coveredDollars)}`} />
        <span style={{ width: `${shareOf(split.personal.dollars)}%`, background: '#e2c48a' }} title={`Personal ${usd(split.personal.dollars)}`} />
      </div>

      {cats.length > 0 && (
        <ul className="rc-fig-cats">{cats.map((c) => (
          <li key={c.key} className={c.done ? 'rc-fig-cat rc-fig-cat--done' : 'rc-fig-cat'}>
            <span className="rc-fig-cat-name">{c.done ? '✓ ' : ''}{c.name}</span>
            <span className="rc-nowrap">{c.done ? `all ${c.charges}` : `${c.covered} of ${c.charges}`} · <strong>{usd(c.done ? c.dollars : c.missingDollars)}</strong>{c.done ? '' : ' missing'}</span>
          </li>
        ))}</ul>
      )}

      {missing.length === 0 ? (!head.businessNone && <p className="rc-note rc-missing-none">✓ Every business charge has a receipt</p>) : (
        <details className="rc-missing" open>
          <summary>Missing receipts ({missing.length}) · {usd(cov.missingDollars)}</summary>
          <div className="rc-scroll">
            <table className="rc-table rc-missing-table">
              <thead><tr><th>Date</th><th>Merchant</th><th className="rc-missing-acct">Category</th><th className="num">Amount</th></tr></thead>
              <tbody>{missing.map((m) => (
                <tr key={m.listItemId}>
                  <td className="rc-nowrap">{shortDate(m.date)}</td>
                  <td className="rc-missing-merchant">{m.merchant || 'Merchant not read'}{m.pending && <span className="rc-tag rc-tag--amber rc-missing-pending">Pending</span>}<span className="rc-missing-acct2">{m.category || 'Uncategorized'}</span></td>
                  <td className="rc-missing-acct">{m.category || 'Uncategorized'}</td>
                  <td className="num">{m.amount !== null && m.amount !== undefined ? usd(m.amount) : <span className="rc-notax">not read</span>}</td>
                </tr>
              ))}</tbody>
              <tfoot><tr><td colSpan={2}>Total</td><td className="rc-missing-acct" /><td className="num">{missingFoot(cov)}</td></tr></tfoot>
            </table>
          </div>
        </details>
      )}

      {covered.length > 0 && (
        <details className="rc-missing rc-fig-covered" open={coveredOpen} onToggle={(e) => setCoveredOpen(e.currentTarget.open)}>
          <summary>{onlyPersonal ? `Covered (${shown.length} of ${covered.length}) · Personal only` : coveredLine(cov)}</summary>
          {onlyPersonal && <button type="button" className="rc-anchor rc-fig-all" onClick={() => setOnlyPersonal(false)}>Show all</button>}
          <div className="rc-scroll">
            <table className="rc-table rc-missing-table">
              <thead><tr><th>Date</th><th>Merchant</th><th className="num">Amount</th></tr></thead>
              <tbody>{shown.map((m) => (
                <tr key={m.listItemId}>
                  <td className="rc-nowrap">{shortDate(m.date)}</td>
                  <td className="rc-missing-merchant">
                    <button type="button" className="rc-anchor" onClick={() => onOpenReceipt && onOpenReceipt(m.receiptId)}>{m.merchant || 'Merchant not read'}</button>
                    {isPersonal(m) && <span className="rc-tag rc-tag--amber rc-missing-pending" title={PERSONAL_TITLE}>Personal</span>}
                  </td>
                  <td className="num">{m.amount !== null && m.amount !== undefined ? usd(m.amount) : <span className="rc-notax">not read</span>}</td>
                </tr>
              ))}</tbody>
              <tfoot><tr><td colSpan={2}>Total</td><td className="num">{usd(onlyPersonal ? split.personal.dollars : cov.chargeDollarsWithReceipt)}</td></tr></tfoot>
            </table>
          </div>
        </details>
      )}

      {leftOut}
      <p className="rc-note" style={{ marginBottom: 0 }}>Everyone's charges on Chase ·1758 (card ·8805). {figNote(cov)}. Read only.</p>
    </div>
  );
}
